import React, { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { Box, Flex, Grid, GridItem, InputRightAddon } from '@chakra-ui/react'

import { PriceInput } from '@/components/Form'

import { PriceCell } from '@/components/Table/components/Cell'
import EditableTable from '@/components/Table/EditableTable'
import { getTotalPrice } from '@/features/offers/helpers/getTotalContractPrice'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { numberWithCommas } from '@/utils/stringHelper'
import { createColumnHelper } from '@tanstack/react-table'
const TotalContractPriceSummary = ({ subSystemIndex, contractPriceDataPath }) => {
  const { adminMode } = useSelector(selectOfferDetail)

  const currency = useWatch({ name: 'currency' })
  const exchangeRate = useWatch({ name: 'exchangeRate' })
  const grossMargin = useWatch({ name: `${contractPriceDataPath}.grossMargin` })
  const totalCost = useWatch({ name: `${contractPriceDataPath}.totalCost` })
  const negotiationMargin = (useWatch({ name: `${contractPriceDataPath}.negotiationMargin` }) || 0) * -1
  const newContractRows = useWatch({ name: `subSystems.${subSystemIndex}.totalContractPrice.newContractRows` })
  const columnHelper = createColumnHelper()

  //Translated
  const isDisabled = !adminMode

  const contractTableData = useMemo(() => {
    if (!Array.isArray(newContractRows))
      return [
        {
          type: 'With Negotiation Margin',
          totalPrice: 0,
          totalPriceHKD: 0,
          GM: 0,
          GMPrice: 0,
        },
        {
          type: 'Without Negotiation Margin',
          totalPrice: 0,
          totalPriceHKD: 0,
          GM: 0,
          GMPrice: 0,
        },
      ]

    const totalPrice = getTotalPrice(newContractRows)
    const totalPriceHKD = totalPrice * exchangeRate
    const withoutMarginTotalPrice = (totalPrice * (100 - (negotiationMargin || 0))) / 100
    const withoutMarginTotalPriceHKD = (totalPriceHKD * (100 - (negotiationMargin || 0))) / 100

    return [
      {
        type: 'With Negotiation Margin',
        totalPrice: totalPrice,
        totalPriceHKD: totalPriceHKD,
        GM: grossMargin,
        GMPrice: totalPriceHKD - totalCost,
      },
      {
        type: 'Without Negotiation Margin',
        totalPrice: withoutMarginTotalPrice,
        totalPriceHKD: withoutMarginTotalPriceHKD,
        GM: ((withoutMarginTotalPriceHKD - totalCost) / withoutMarginTotalPriceHKD) * 100,
        GMPrice: withoutMarginTotalPriceHKD - totalCost,
      },
    ]
  }, [newContractRows, grossMargin, totalCost])

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('type', {
        header: () => <></>,
      }),
      columnHelper.accessor('totalPrice', {
        header: () => <>{`Total Price${currency && currency !== 'HKD' ? `(${currency})` : ''}`}</>,
        cell: PriceCell,
      }),
      columnHelper.accessor('totalPriceHKD', {
        header: () => <>Total Price (HKD)</>,
        cell: PriceCell,
      }),
      columnHelper.accessor('GM', {
        header: () => <>GM (%)</>,
        cell: ({ getValue }) => <span>{numberWithCommas(getValue())}%</span>,
      }),
      columnHelper.accessor('GMPrice', {
        header: () => <>GM (HKD)</>,
        cell: PriceCell,
      }),
    ],
    [columnHelper, currency]
  )
  return (
    <Flex direction={'column'} gap={2}>
      <Grid templateColumns="repeat(12, 1fr)" gap={4} mb="5">
        <GridItem colSpan={6}>
          <PriceInput
            name={`${contractPriceDataPath}.grossMargin`}
            label="GM(%)"
            placeholder="Enter value"
            disabled={isDisabled}
            trailingIcon={<InputRightAddon>%</InputRightAddon>}
            isRow
          />
        </GridItem>
        <GridItem colSpan={6}>
          <PriceInput
            name={`${contractPriceDataPath}.negotiationMargin`}
            label="Negotiation Margin"
            placeholder="Enter value"
            disabled={isDisabled}
            trailingIcon={<InputRightAddon>%</InputRightAddon>}
            isRow
          />
        </GridItem>
      </Grid>
      <Box overflowX={'scroll'}>
        <EditableTable columnDef={columnDef} data={contractTableData} disabled={true} tableWidth={'100%'} />
      </Box>
    </Flex>
  )
}

export default TotalContractPriceSummary
